import * as yup from "yup";

const REQUIRED_FIELD_MESSAGE = "Campo obrigatório";
const INVALID_PHONE_MESSAGE = "Número de telefone inválido";
const INVALID_NUIT_MESSAGE = "O NUIT deve ter 9 digitos";
const INVALID_PASSWORD_MESSAGE = "A senha deve conter letras e digitos apenas";
const NOT_A_NUMBER_MESSAGE = "Valor deve ser numérico";
const INVALID_MIN_NUMBER_MESSAGE = "Número não pode ser menor que zero";
const INVALID_SCORE_MESSAGE =
  "Pontuação Inválida. Insira um número entre 0 e 20";
const INVALID_BUSINESS_PLAN_SCORE_MESSAGE =
  "Pontuação Inválida. Insira um número entre 0 e 20";

const isRequiredString = yup
  .string()
  .typeError(REQUIRED_FIELD_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);
const isRequiredNumber = yup.number().required(REQUIRED_FIELD_MESSAGE);
const isNumber = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .nullable();
const isMonetary = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .nullable();
const isNullablePositiveInteger = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .integer("Número deve ser inteiro")
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .nullable();
const isValidPhoneNumber = yup.string().matches(/^8(2|3|4|5|6|7)[0-9]{7}$/, {
  message: INVALID_PHONE_MESSAGE,
  excludeEmptyString: true,
});
const isValidNuit = yup
  .string()
  .matches(/^[0-9]{9}$/, {
    message: INVALID_NUIT_MESSAGE,
    excludeEmptyString: true,
  })
  .nullable();
const isValidEmail = yup.string().email("Email inválido").nullable();
const isValidDate = yup
  .string()
  .matches(
    /^(0[1-9]|1\d|2\d|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/,
    "Data inválida. Use o formato dd/mm/aaaa"
  );

const isValidFundingRequest = yup
  .number()
  .max(1500000, "Montante não pode ser superior a 1.500.000 MZN")
  .nullable();

const isValidScore = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(INVALID_SCORE_MESSAGE)
  .integer(INVALID_SCORE_MESSAGE)
  .min(0, INVALID_SCORE_MESSAGE)
  .max(20, INVALID_SCORE_MESSAGE);

const isValid25Score = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))

  .typeError(INVALID_SCORE_MESSAGE)
  .integer(INVALID_SCORE_MESSAGE)
  .min(0, "Pontuação Inválida. Insira um número entre 0 e 25")
  .max(25, "Pontuação Inválida. Insira um número entre 0 e 25");

const isValid15Score = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(INVALID_SCORE_MESSAGE)
  .integer(INVALID_SCORE_MESSAGE)
  .min(0, "Pontuação Inválida. Insira um número entre 0 e 15")
  .max(15, "Pontuação Inválida. Insira um número entre 0 e 15");

const isValid20Score = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(INVALID_SCORE_MESSAGE)
  .integer(INVALID_SCORE_MESSAGE)
  .min(0, "Pontuação Inválida. Insira um número entre 0 e 20")
  .max(20, "Pontuação Inválida. Insira um número entre 0 e 20");

const isValidBusinessPlanScore = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))

  .typeError(INVALID_BUSINESS_PLAN_SCORE_MESSAGE)
  .integer(INVALID_BUSINESS_PLAN_SCORE_MESSAGE)
  .min(0, INVALID_BUSINESS_PLAN_SCORE_MESSAGE)
  .max(20, INVALID_BUSINESS_PLAN_SCORE_MESSAGE);

const roleRules = yup.object({
  roleCode: yup.string().required(REQUIRED_FIELD_MESSAGE),
});

const passwordRules = yup.object({
  password: yup
    .string()
    .transform((x) => (x === "" ? undefined : x))
    .required(REQUIRED_FIELD_MESSAGE)
    .min(6, "A senha deve ter no mínimo 6 caracteres")
    .max(20, "A senha deve ter no máximo 20 caracteres")
    .matches(
      /^(?=.*\d)[^#&!~<>?=+*()-+=\/?@{}_$%]+$/,
      INVALID_PASSWORD_MESSAGE
    ),
  passwordConfirm: yup
    .string()
    .transform((x) => (x === "" ? undefined : x))
    .required(REQUIRED_FIELD_MESSAGE)
    .oneOf([yup.ref("password")], "As senhas devem ser iguais"),
});

const userSchema = yup.object({
  firstName: isRequiredString,
  lastName: isRequiredString,
  username: isRequiredString.concat(isValidPhoneNumber),
  email: isRequiredString.concat(isValidEmail),
});

const basicApplicationSchema = yup.object({
  firstName: isRequiredString,
  lastName: isRequiredString,
  nuit: isValidNuit,
  email: isRequiredString.concat(isValidEmail),
  birthDate: isRequiredString.concat(isValidDate),
  gender: isRequiredString,
  identificationDocTypeCode: isRequiredString,
  otherIdentificationDocType: yup
    .string()
    .when("identificationDocTypeCode", {
      is: "0",
      then: yup.string().required("Especifique o tipo"),
    })
    .nullable(true),
  identificationDocNumber: isRequiredString,
  phoneNumber1: isRequiredString.concat(isValidPhoneNumber),
  phoneNumber2: isValidPhoneNumber,
  altContactPersonPhoneNumber: isValidPhoneNumber,
  numberOfExpectedWorkers: isNullablePositiveInteger,
  numberOfExpectedFemaleWorkers: isNullablePositiveInteger,
  numberOfExpectedMaleWorkers: isNullablePositiveInteger,
  numberOfExpectedWorkersAged18To35: isNullablePositiveInteger,
  numberOfExistingWorkers: isNullablePositiveInteger,
  numberOfExistingMaleWorkers: isNullablePositiveInteger,
  numberOfExistingFemaleWorkers: isNullablePositiveInteger,
  numberOfExistingMaleWorkersAged18To35: isNullablePositiveInteger,
  numberOfExistingFemaleWorkersAged18To35: isNullablePositiveInteger,
});

const submitApplicationSchema = yup.object({
  email: isRequiredString,
  altContactPersonName: isRequiredString,
  altContactPersonPhoneNumber: isRequiredString,
  altContactPersonRelationship: isRequiredString,
  provinceId: isRequiredString,
  districtId: isRequiredString,
  neighborhood: isRequiredString,
  street: isRequiredString,
  addressTypeCode: isRequiredString,
  academicLevelCode: isRequiredString,
  competitorData: isRequiredString,
  hasBusinessCurrently: isRequiredString,
  businessIdea: isRequiredString,
  ideaProductOrService: isRequiredString,
  targetCustomerProfile: isRequiredString,
  workers: isRequiredString,
  numberOfExpectedWorkers: isRequiredNumber.concat(isNumber),
  numberOfExpectedFemaleWorkers: isRequiredNumber.concat(isNumber),
  numberOfExpectedMaleWorkers: isRequiredNumber.concat(isNumber),
  numberOfExpectedWorkersAged18To35: isRequiredNumber.concat(isNumber),
  howWillEnsureEqualEmploymentOpportunity: isRequiredString,
});

const submitBusinessProfileSchema = yup.object({
  businessProductOrService: isRequiredString,
  numberOfExistingWorkers: isRequiredNumber.concat(isNumber),
  numberOfExistingMaleWorkers: isRequiredNumber.concat(isNumber),
  numberOfExistingFemaleWorkers: isRequiredNumber.concat(isNumber),
  numberOfExistingMaleWorkersAged18To35: isRequiredNumber.concat(isNumber),
  numberOfExistingFemaleWorkersAged18To35: isRequiredNumber.concat(isNumber),
  workFacilitiesProvinceId: isRequiredString,
  workFacilitiesDistrictId: isRequiredString,
  workFacilitiesLocality: isRequiredString,
  workFacilitiesNeighborhood: isRequiredString,
  workFacilitiesStreet: isRequiredString,
  ownWorkFacilities: isRequiredString,

  // isBusinessManufacturerOrProducer: yup.boolean(),
  // isBusinessServiceProvider: yup.boolean(),
  // isBusinessRetailer: yup.boolean(),
  // isBusinessWholesaler: yup.boolean(),
});
// .test("myCustomTest", null, (obj) => {
//   if (
//     obj.isBusinessManufacturerOrProducer ||
//     obj.isBusinessServiceProvider ||
//     obj.isBusinessRetailer ||
//     obj.isBusinessWholesaler
//   ) {
//     return true; // everything is fine
//   }

//   return new yup.ValidationError(
//     "Selecione pelo menos uma das opções",
//     null,
//     "businessType"
//   );
// });

const grievanceSchema = yup.object({
  type: isRequiredString,
  subject: isRequiredString,
  description: isRequiredString,
});

const applicationScoreSchema = yup.object({
  app001: isValidScore,
  app003: isValidScore,
  app004: isValidScore,
  app005: isValidScore,
  app006: isValidScore,
  app007: isValidScore,
  app008: isValidScore,
  app009: isValidScore,
});

const businessPlanScoreSchema = yup.object({
  bp001: isValidBusinessPlanScore,
  bp002: isValidBusinessPlanScore,
  bp003: isValidBusinessPlanScore,
  bp004: isValidBusinessPlanScore,
  bp005: isValidBusinessPlanScore,
  bp006: isValidBusinessPlanScore,
  bp007: isValidBusinessPlanScore,
  bp008: isValidBusinessPlanScore,
  bp009: isValidBusinessPlanScore,
  bp010: isValidBusinessPlanScore,
  bp011: isValidBusinessPlanScore,
  bp012: isValidBusinessPlanScore,
  bp013: isValidBusinessPlanScore,
  bp014: isValidBusinessPlanScore,
  bp015: isValidBusinessPlanScore,
  bp016: isValidBusinessPlanScore,
});

const basicBusinessPlanSchema = yup.object({
  neededCapital: isMonetary,
  ownFunds: isMonetary,
  requestedFunds: isMonetary.concat(isValidFundingRequest),
  otherFunds: isMonetary,
  workers: yup.object({
    currentMales: isNullablePositiveInteger,
    currentMalesAged18To35: isNullablePositiveInteger,
    currentFemales: isNullablePositiveInteger,
    currentFemalesAged18To35: isNullablePositiveInteger,
    males2022: isNullablePositiveInteger,
    males2022Aged18To35: isNullablePositiveInteger,
    females2022: isNullablePositiveInteger,
    females2022Aged18To35: isNullablePositiveInteger,
    males2023: isNullablePositiveInteger,
    males2023Aged18To35: isNullablePositiveInteger,
    females2023: isNullablePositiveInteger,
    females2023Aged18To35: isNullablePositiveInteger,
    males2024: isNullablePositiveInteger,
    males2024Aged18To35: isNullablePositiveInteger,
    females2024: isNullablePositiveInteger,
    females2024Aged18To35: isNullablePositiveInteger,
  }),
  investmentPlan: yup.object({
    constructionOrPurchase: isMonetary,
    adaptationOrRehabilitation: isMonetary,
    machineryAndTools: isMonetary,
    furniture: isMonetary,
    vehicles: isMonetary,
    computersAndDevices: isMonetary,
    motorcycle: isMonetary,
    householdAppliances: isMonetary,
    rawMaterialOrMerchandise: isMonetary,
    licensesAndFees: isMonetary,
    marketing: isMonetary,
    salaries: isMonetary,
    income: isMonetary,
    schoolFees: isMonetary,
    familyRanch: isMonetary,
    leisure: isMonetary,
    others1: isMonetary,
    others2: isMonetary,
    others3: isMonetary,
  }),
  salesPlan: yup.object({
    productService1Price2022: isMonetary,
    productService1Qty2022: isMonetary,
    productService1Price2023: isMonetary,
    productService1Qty2023: isMonetary,
    productService1Price2024: isMonetary,
    productService1Qty2024: isMonetary,
    productService2Price2022: isMonetary,
    productService2Qty2022: isMonetary,
    productService2Price2023: isMonetary,
    productService2Qty2023: isMonetary,
    productService2Price2024: isMonetary,
    productService2Qty2024: isMonetary,
    productService3Price2022: isMonetary,
    productService3Qty2022: isMonetary,
    productService3Price2023: isMonetary,
    productService3Qty2023: isMonetary,
    productService3Price2024: isMonetary,
    productService3Qty2024: isMonetary,
    otherProductServicePrice2022: isMonetary,
    otherProductServiceQty2022: isMonetary,
    otherProductServicePrice2023: isMonetary,
    otherProductServiceQty2023: isMonetary,
    otherProductServicePrice2024: isMonetary,
    otherProductServiceQty2024: isMonetary,
  }),

  provisionalPlan: yup.object({
    sales2022: isMonetary,
    goodsAndMaterials2022: isMonetary,
    income2022: isMonetary,
    energy2022: isMonetary,
    water2022: isMonetary,
    internet2022: isMonetary,
    salaries2022: isMonetary,
    equipmentMaintenance2022: isMonetary,
    officeSupplies2022: isMonetary,
    transport2022: isMonetary,
    others2022: isMonetary,
    sales2023: isMonetary,
    goodsAndMaterials2023: isMonetary,
    income2023: isMonetary,
    energy2023: isMonetary,
    water2023: isMonetary,
    internet2023: isMonetary,
    salaries2023: isMonetary,
    equipmentMaintenance2023: isMonetary,
    officeSupplies2023: isMonetary,
    transport2023: isMonetary,
    others2023: isMonetary,
    sales2024: isMonetary,
    goodsAndMaterials2024: isMonetary,
    income2024: isMonetary,
    energy2024: isMonetary,
    water2024: isMonetary,
    internet2024: isMonetary,
    salaries2024: isMonetary,
    equipmentMaintenance2024: isMonetary,
    officeSupplies2024: isMonetary,
    transport2024: isMonetary,
    others2024: isMonetary,
  }),
});

const submitBusinessPlanSchema = yup.object({
  nur: isRequiredString,
  provinceId: isRequiredString,
  districtId: isRequiredString,
  neighborhood: isRequiredString,
  addressTypeCode: isRequiredString,
  partnersQualifications: isRequiredString,
  hasBusinessCurrently: isRequiredString,
  businessIdea: isRequiredString,
  ideaProductOrService: isRequiredString,
  mission: isRequiredString,
  vision: isRequiredString,
  neededCapital: isRequiredNumber.concat(isNumber),
  ownFunds: isRequiredNumber.concat(isNumber),
  requestedFunds: isRequiredNumber.concat(isNumber),
  otherFunds: isRequiredNumber.concat(isNumber),
  companyQualities: isRequiredString,
  whatNeedsToBeStrengthened: isRequiredString,
  favorableExternalContextAspects: isRequiredString,
  unfavorableExternalContextAspects: isRequiredString,
  mainProblemsIntendToSolve: isRequiredString,
  solutionsForEachProblem: isRequiredString,
  mainGroupsOfCustomers: isRequiredString,
  promotionStrategy: isRequiredString,
  locations: isRequiredString,
  priceStrategy: isRequiredString,
  typeOfCompetitor: isRequiredString,
  possibleNegativeEnvironmentalEffects: isRequiredString,
  possibleNegativeSocialEffects: isRequiredString,
  workers: yup.object({
    currentMales: isRequiredNumber.concat(isNumber),
    currentMalesAged18To35: isRequiredNumber.concat(isNumber),
    currentFemales: isRequiredNumber.concat(isNumber),
    currentFemalesAged18To35: isRequiredNumber.concat(isNumber),
    males2022: isRequiredNumber.concat(isNumber),
    males2022Aged18To35: isRequiredNumber.concat(isNumber),
    females2022: isRequiredNumber.concat(isNumber),
    females2022Aged18To35: isRequiredNumber.concat(isNumber),
    males2023: isRequiredNumber.concat(isNumber),
    males2023Aged18To35: isRequiredNumber.concat(isNumber),
    females2023: isRequiredNumber.concat(isNumber),
    females2023Aged18To35: isRequiredNumber.concat(isNumber),
    males2024: isRequiredNumber.concat(isNumber),
    males2024Aged18To35: isRequiredNumber.concat(isNumber),
    females2024: isRequiredNumber.concat(isNumber),
    females2024Aged18To35: isRequiredNumber.concat(isNumber),
  }),
  investmentPlan: yup.object({
    constructionOrPurchase: isMonetary,
    adaptationOrRehabilitation: isMonetary,
    machineryAndTools: isMonetary,
    furniture: isMonetary,
    vehicles: isMonetary,
    computersAndDevices: isMonetary,
    motorcycle: isMonetary,
    householdAppliances: isMonetary,
    rawMaterialOrMerchandise: isMonetary,
    licensesAndFees: isMonetary,
    marketing: isMonetary,
    salaries: isMonetary,
    income: isMonetary,
    schoolFees: isMonetary,
    familyRanch: isMonetary,
    leisure: isMonetary,
  }),
  salesPlan: yup.object({
    productService1Desc: isRequiredString,
    productService1Price2022: isRequiredNumber.concat(isMonetary),
    productService1Qty2022: isRequiredNumber.concat(isMonetary),
    productService1Price2023: isRequiredNumber.concat(isMonetary),
    productService1Qty2023: isRequiredNumber.concat(isMonetary),
    productService1Price2024: isRequiredNumber.concat(isMonetary),
    productService1Qty2024: isRequiredNumber.concat(),
    productService2Price2022: isMonetary,
    productService2Qty2022: isMonetary,
    productService2Price2023: isMonetary,
    productService2Qty2023: isMonetary,
    productService2Price2024: isMonetary,
    productService2Qty2024: isMonetary,
    productService3Price2022: isMonetary,
    productService3Qty2022: isMonetary,
    productService3Price2023: isMonetary,
    productService3Qty2023: isMonetary,
    productService3Price2024: isMonetary,
    productService3Qty2024: isMonetary,
    otherProductServicePrice2022: isMonetary,
    otherProductServiceQty2022: isMonetary,
    otherProductServicePrice2023: isMonetary,
    otherProductServiceQty2023: isMonetary,
    otherProductServicePrice2024: isMonetary,
    otherProductServiceQty2024: isMonetary,
  }),
  provisionalPlan: yup.object({
    sales2022: isRequiredNumber.concat(isMonetary),
    goodsAndMaterials2022: isRequiredNumber.concat(isMonetary),
    income2022: isMonetary,
    energy2022: isMonetary,
    water2022: isMonetary,
    internet2022: isMonetary,
    salaries2022: isMonetary,
    equipmentMaintenance2022: isMonetary,
    officeSupplies2022: isMonetary,
    transport2022: isMonetary,
    others2022: isMonetary,
    sales2023: isRequiredNumber.concat(isMonetary),
    goodsAndMaterials2023: isRequiredNumber.concat(isMonetary),
    income2023: isMonetary,
    energy2023: isMonetary,
    water2023: isMonetary,
    internet2023: isMonetary,
    salaries2023: isMonetary,
    equipmentMaintenance2023: isMonetary,
    officeSupplies2023: isMonetary,
    transport2023: isMonetary,
    others2023: isMonetary,
    sales2024: isRequiredNumber.concat(isMonetary),
    goodsAndMaterials2024: isRequiredNumber.concat(isMonetary),
    income2024: isMonetary,
    energy2024: isMonetary,
    water2024: isMonetary,
    internet2024: isMonetary,
    salaries2024: isMonetary,
    equipmentMaintenance2024: isMonetary,
    officeSupplies2024: isMonetary,
    transport2024: isMonetary,
    others2024: isMonetary,
  }),
});

const submitExistingBusinessProfileSchema = yup.object({
  legalFormOfBusinessCode: isRequiredString,
  businessProductOrService: isRequiredString,
  businessHistory: isRequiredString,
});

const companySchema = yup.object({
  name: isRequiredString,
  nuit: isRequiredString.concat(isValidNuit),
  description: isRequiredString,
  provinceId: isRequiredString,
  districtId: isRequiredString,
  address: isRequiredString,
  permitNumber: isRequiredString,
  commercialCertificate: isRequiredString,
  legalEntityType: isRequiredString,
  sectorOfActivity: isRequiredString,
  startDateOfActivities: isRequiredString.concat(isValidDate),
});

const vacancySchema = yup.object({
  title: isRequiredString,
  termsOfReference: isRequiredString,
  requirements: isRequiredString,
  provinceId: isRequiredString,
  districtId: isRequiredString,
  recruitmentStartDate: isRequiredString,
  recruitmentEndDate: isRequiredString,
  numberOfVacancies: isRequiredNumber.concat(isNullablePositiveInteger),
  duration: isRequiredNumber.concat(isNullablePositiveInteger),
});

const traineeProfileSchema = yup.object({
  name: isRequiredString,
  birthDate: isRequiredString.concat(isValidDate),
  gender: isRequiredString,
  identificationDocNumber: isRequiredString,
  nuit: isRequiredString.concat(isValidNuit),
  residence: isRequiredString,
  phoneNumber1: isRequiredString.concat(isValidPhoneNumber),
  phoneNumber2: isValidPhoneNumber,
  email: isRequiredString.concat(isValidEmail),
  education: isRequiredString,
  courses: isRequiredString,
  experience: isRequiredString,
  languages: isRequiredString,
  skills: isRequiredString,
  provinceId: isRequiredString,
  districtId: isRequiredString,
});

const internshipApplicationScoreSchema = yup.object({
  tr001: isValid20Score,
  tr002: isValid20Score,
  tr003: isValid20Score,
  tr004: isValid20Score,
  tr005: isValid20Score,
});

export {
  userSchema,
  roleRules,
  passwordRules,
  basicApplicationSchema,
  submitBusinessProfileSchema,
  submitApplicationSchema,
  grievanceSchema,
  applicationScoreSchema,
  basicBusinessPlanSchema,
  submitBusinessPlanSchema,
  submitExistingBusinessProfileSchema,
  businessPlanScoreSchema,
  companySchema,
  vacancySchema,
  traineeProfileSchema,
  internshipApplicationScoreSchema,
};
